import React from 'react';
import { Stack } from '@fluentui/react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { 
    faCheck, 
    faClock, 
    faExclamationCircle, 
    faInbox, 
    faLocationDot, 
    faShip, 
    faTag, 
    faTruck 
} from '@fortawesome/pro-light-svg-icons';
import { NavLink } from 'react-router-dom';
import styles from '../styles.module.scss';
import { IOperationItem } from '../types';
import Text from '../../../typography/text';

interface IWorkspaceBoardViewProps {
    items: IOperationItem[];
    onQuickStatus(item: IOperationItem): void;
    onReschedule(item: IOperationItem): void;
}

const WorkspaceBoardView: React.FC<IWorkspaceBoardViewProps> = ({ items, onQuickStatus, onReschedule }) => {
    if (items.length === 0) {
        return (
            <div className={styles.tableWrapper}>
                <div className={styles.emptyState}>
                    <FontAwesomeIcon icon={faInbox} style={{ fontSize: 36 }} />
                    <Text size="medium" style={{ fontWeight: 600 }}>Tidak ada aktivitas operasional</Text>
                    <Text size="small">Semua jadwal pada filter ini telah terselesaikan atau tidak ada data jadwal.</Text>
                </div>
            </div>
        );
    }

    return (
        <div className={styles.boardGrid}>
            {items.map((item) => {
                const isOverdue = item.isOverdue;
                const statusClass = item.normalizedStatus === 'completed' 
                    ? styles.cardCompleted 
                    : (item.normalizedStatus === 'in_progress' ? styles.cardInProgress : '');

                return (
                    <div 
                        key={item.id} 
                        className={`${styles.boardCard} ${isOverdue ? styles.cardOverdue : ''} ${statusClass}`}
                    >
                        {/* Header: Activity Badge & Status */}
                        <div className={styles.boardCardHeader}>
                            <span 
                                className={styles.activityBadge}
                                style={{
                                    background: item.activityColor + '15',
                                    color: item.activityColor,
                                    border: `1px solid ${item.activityColor}35`,
                                }}
                            >
                                <FontAwesomeIcon icon={item.activityIcon} />
                                {item.activityLabel}
                            </span>

                            <span 
                                className={styles.statusBadge}
                                style={{
                                    background: item.normalizedStatus === 'completed' ? '#dcfce7' : (item.normalizedStatus === 'in_progress' ? '#e0f2fe' : '#f1f5f9'),
                                    color: item.normalizedStatus === 'completed' ? '#16a34a' : (item.normalizedStatus === 'in_progress' ? '#0284c7' : '#64748b'),
                                    border: item.normalizedStatus === 'completed' ? '1px solid #bbf7d0' : (item.normalizedStatus === 'in_progress' ? '1px solid #bae6fd' : '1px solid #e2e8f0'),
                                }}
                            >
                                <span 
                                    style={{ 
                                        width: 6, 
                                        height: 6, 
                                        borderRadius: '50%', 
                                        backgroundColor: item.normalizedStatus === 'completed' ? '#22c55e' : (item.normalizedStatus === 'in_progress' ? '#0284c7' : '#94a3b8'),
                                        display: 'inline-block' 
                                    }} 
                                />
                                {item.statusLabel}
                            </span>
                        </div>

                        {/* Body: Main Information */}
                        <div className={styles.boardCardBody}>
                            {/* Entity Title */}
                            {item.shipName ? (
                                <Stack tokens={{ childrenGap: 2 }}>
                                    <NavLink to={item.detailsLink} style={{ textDecoration: 'none', color: '#0284c7', fontWeight: 600 }}>
                                        <Text size="medium" style={{ fontWeight: 600, color: '#0f172a' }}>
                                            {item.shipName}
                                        </Text>
                                    </NavLink>
                                    {item.voy && (
                                        <Text size="xsmall" style={{ color: '#64748b' }}>
                                            Voy: {item.voy} {item.vendorName ? `(${item.vendorName})` : ''}
                                        </Text>
                                    )}
                                </Stack>
                            ) : (
                                <Stack tokens={{ childrenGap: 2 }}>
                                    <Text size="medium" style={{ fontWeight: 600, color: '#0f172a' }}>
                                        {item.truckRegNumber || item.vendorName || '-'}
                                    </Text>
                                    {item.driverName && (
                                        <Text size="xsmall" style={{ color: '#64748b' }}>
                                            Driver: {item.driverName} {item.driverPhone ? `(${item.driverPhone})` : ''}
                                        </Text>
                                    )}
                                </Stack>
                            )}

                            {/* Order & Customer */}
                            {item.orderNumber && (
                                <Stack horizontal verticalAlign="center" tokens={{ childrenGap: 6 }}>
                                    <NavLink to={item.orderLink || '#'} style={{ textDecoration: 'none' }}>
                                        <Text size="small" style={{ color: '#0284c7', fontWeight: 600 }}>
                                            {item.orderNumber}
                                        </Text>
                                    </NavLink>
                                    {item.customerName && (
                                        <Text size="xsmall" style={{ color: '#64748b' }}>
                                            • {item.customerName}
                                        </Text>
                                    )}
                                </Stack>
                            )}

                            {/* Location & Route */}
                            {item.locationText && (
                                <Text size="small" style={{ color: '#334155' }}>
                                    📍 {item.locationText}
                                </Text>
                            )}

                            {/* Containers */}
                            {item.containers && item.containers.length > 0 && (
                                <Stack horizontal wrap tokens={{ childrenGap: 4 }}>
                                    {item.containers.slice(0, 3).map((c, idx) => (
                                        <span 
                                            key={idx}
                                            style={{
                                                fontSize: '11px',
                                                background: '#f1f5f9',
                                                border: '1px solid #e2e8f0',
                                                borderRadius: '4px',
                                                padding: '1px 6px',
                                                color: '#334155',
                                                fontWeight: 500
                                            }}
                                        >
                                            {c.containerNumber}
                                        </span>
                                    ))}
                                    {item.containers.length > 3 && (
                                        <span style={{ fontSize: '11px', color: '#64748b' }}>
                                            +{item.containers.length - 3} lainnya
                                        </span>
                                    )}
                                </Stack>
                            )}

                            {/* Timing & Overdue */}
                            <Stack horizontal horizontalAlign="space-between" verticalAlign="center" style={{ marginTop: 4 }}>
                                <div>
                                    <Text size="xsmall" style={{ color: '#64748b' }}>Jadwal / Waktu:</Text>
                                    <Text size="small" style={{ fontWeight: 600, color: '#0f172a' }}>
                                        {item.scheduledDateFormatted}
                                    </Text>
                                </div>

                                {isOverdue && (
                                    <span className={styles.overdueTag}>
                                        <FontAwesomeIcon icon={faExclamationCircle} />
                                        Overdue {item.overdueDays ? `${item.overdueDays}h` : ''}
                                    </span>
                                )}
                            </Stack>
                        </div>

                        {/* Footer: Action Buttons */}
                        <div className={styles.boardCardFooter}>
                            <div className={styles.actionButtonGroup} style={{ width: '100%', justifyContent: 'space-between' }}>
                                <button
                                    className={styles.btnActionReschedule}
                                    onClick={() => onReschedule(item)}
                                    title="Reschedule / Jadwal Ulang"
                                    style={{ flex: 1, justifyContent: 'center' }}
                                >
                                    <FontAwesomeIcon icon={faClock} />
                                    <span>Reschedule</span>
                                </button>

                                <button
                                    className={styles.btnActionCheck}
                                    onClick={() => onQuickStatus(item)}
                                    title="Mark Active / Update Status"
                                    style={{ flex: 1, justifyContent: 'center' }}
                                >
                                    <FontAwesomeIcon icon={faCheck} />
                                    <span>Update</span>
                                </button>
                            </div>
                        </div>
                    </div>
                );
            })}
        </div>
    );
};

export default WorkspaceBoardView;
